import React, { useState, useEffect } from 'react';
import './PersonalDashboard.css';

function PersonalDashboard({ onClose, socket, chats }) {
  const [stats, setStats] = useState({
    totalChats: 0,
    totalMessages: 0,
    chatsWithNoResponse: 0,
    chatsOver24Hours: 0,
    todayMessages: 0,
    weekMessages: 0
  });

  useEffect(() => {
    if (!chats) return;

    // חישוב הסטטיסטיקות - רק משתמשי Firebase (מי שיש להם גישה לפלטפורמה)
    const firebaseChats = chats.filter(chat => chat.inFirebase === 1);
    const now = Date.now() / 1000; // בשניות
    const oneDayAgo = now - 86400; // 24 שעות
    const oneWeekAgo = now - 604800; // 7 ימים

    const totalChats = firebaseChats.length;

    // חישוב צ'אטים שמעל 24 שעות ללא מענה (רק משתמשי Firebase)
    const chatsOver24Hours = firebaseChats.filter(chat => {
      if (!chat.timestamp) return false;
      return chat.timestamp < oneDayAgo;
    }).length;

    setStats({
      totalChats,
      totalMessages: 0, // יעודכן מהשרת
      chatsWithNoResponse: 0, // יעודכן מהשרת
      chatsOver24Hours,
      todayMessages: 0, // יעודכן מהשרת
      weekMessages: 0 // יעודכן מהשרת
    });

    // בקשת סטטיסטיקות נוספות מהשרת
    if (socket) {
      socket.emit('get_dashboard_stats');

      socket.once('dashboard_stats', (serverStats) => {
        setStats(prev => ({
          ...prev,
          ...serverStats
        }));
      });
    }
  }, [chats, socket]);

  const progressStages = {
    'watching_training': { label: 'צפייה בהכשרה', icon: '📺', color: '#4A90E2' },
    'back_test': { label: 'בק טסט', icon: '📊', color: '#7B68EE' },
    'forward': { label: 'פורוורד', icon: '⏩', color: '#50C878' },
    'live_trading': { label: 'מסחר בפועל', icon: '💹', color: '#00A884' },
    'completed': { label: 'סיים', icon: '✅', color: '#34C759' }
  };

  // חישוב התפלגות לפי שלבי התקדמות (רק משתמשי Firebase)
  const stageDistribution = {};
  const firebaseChats = chats.filter(chat => chat.inFirebase === 1);
  firebaseChats.forEach(chat => {
    const stage = chat.progressStage || 'watching_training';
    stageDistribution[stage] = (stageDistribution[stage] || 0) + 1;
  });

  return (
    <div className="personal-dashboard-overlay" onClick={onClose}>
      <div className="personal-dashboard-panel" onClick={(e) => e.stopPropagation()}>
        <div className="dashboard-header">
          <h2>📊 האזור האישי שלי</h2>
          <button className="close-dashboard-btn" onClick={onClose}>
            <svg viewBox="0 0 24 24" width="24" height="24">
              <path fill="currentColor" d="M19.6 17.8L13.3 11.5l6.3-6.3c.5-.5.5-1.3 0-1.8-.5-.5-1.3-.5-1.8 0l-6.3 6.3-6.3-6.3c-.5-.5-1.3-.5-1.8 0-.5.5-.5 1.3 0 1.8l6.3 6.3-6.3 6.3c-.5.5-.5 1.3 0 1.8.3.3.6.4.9.4s.6-.1.9-.4l6.3-6.3 6.3 6.3c.3.3.6.4.9.4s.6-.1.9-.4c.5-.5.5-1.3 0-1.8z"/>
            </svg>
          </button>
        </div>

        <div className="dashboard-content">
          {/* סטטיסטיקות כלליות */}
          <section className="dashboard-section">
            <h3>סטטיסטיקות כלליות</h3>
            <div className="stats-grid">
              <div className="stat-card primary">
                <div className="stat-icon">💬</div>
                <div className="stat-info">
                  <label>סה"כ שיחות</label>
                  <p className="stat-value">{stats.totalChats}</p>
                </div>
              </div>

              <div className="stat-card warning">
                <div className="stat-icon">⏰</div>
                <div className="stat-info">
                  <label>מעל 24 שעות ללא מענה</label>
                  <p className="stat-value">{stats.chatsOver24Hours}</p>
                </div>
              </div>

              <div className="stat-card success">
                <div className="stat-icon">📩</div>
                <div className="stat-info">
                  <label>הודעות היום</label>
                  <p className="stat-value">{stats.todayMessages}</p>
                </div>
              </div>

              <div className="stat-card info">
                <div className="stat-icon">📈</div>
                <div className="stat-info">
                  <label>הודעות השבוע</label>
                  <p className="stat-value">{stats.weekMessages}</p>
                </div>
              </div>
            </div>
          </section>

          {/* התפלגות לפי שלבי התקדמות */}
          <section className="dashboard-section">
            <h3>התפלגות לפי שלב התקדמות</h3>
            <div className="progress-distribution">
              {Object.entries(progressStages).map(([stageId, stage]) => {
                const count = stageDistribution[stageId] || 0;
                const percentage = stats.totalChats > 0 ? Math.round((count / stats.totalChats) * 100) : 0;

                return (
                  <div key={stageId} className="distribution-item">
                    <div className="distribution-header">
                      <div className="distribution-label">
                        <span className="distribution-icon">{stage.icon}</span>
                        <span>{stage.label}</span>
                      </div>
                      <div className="distribution-count">
                        {count} ({percentage}%)
                      </div>
                    </div>
                    <div className="distribution-bar">
                      <div
                        className="distribution-fill"
                        style={{
                          width: `${percentage}%`,
                          backgroundColor: stage.color
                        }}
                      ></div>
                    </div>
                  </div>
                );
              })}
            </div>
          </section>

          {/* שיחות שדורשות תשומת לב */}
          <section className="dashboard-section">
            <h3>🚨 דורשים תשומת לב</h3>
            <div className="attention-list">
              {chats
                .filter(chat => {
                  const now = Date.now() / 1000;
                  const oneDayAgo = now - 86400;
                  // רק משתמשי Firebase שמעל 24 שעות ללא מענה
                  return chat.inFirebase === 1 && chat.timestamp && chat.timestamp < oneDayAgo;
                })
                .slice(0, 10)
                .map(chat => {
                  const hoursAgo = Math.floor((Date.now() / 1000 - chat.timestamp) / 3600);
                  return (
                    <div key={chat.id} className="attention-item">
                      <div className="attention-info">
                        <strong>{chat.name}</strong>
                        <span className="attention-time">לפני {hoursAgo} שעות</span>
                      </div>
                      {chat.progressStage && progressStages[chat.progressStage] && (
                        <div
                          className="attention-badge"
                          style={{ backgroundColor: progressStages[chat.progressStage].color }}
                        >
                          {progressStages[chat.progressStage].icon} {progressStages[chat.progressStage].label}
                        </div>
                      )}
                    </div>
                  );
                })}
              {chats.filter(chat => {
                const now = Date.now() / 1000;
                const oneDayAgo = now - 86400;
                return chat.inFirebase === 1 && chat.timestamp && chat.timestamp < oneDayAgo;
              }).length === 0 && (
                <div className="no-attention">
                  <svg viewBox="0 0 24 24" width="48" height="48">
                    <path fill="#00a884" d="M9 16.2L4.8 12l-1.4 1.4L9 19 21 7l-1.4-1.4L9 16.2z"/>
                  </svg>
                  <p>כל הצ'אטים מטופלים! 🎉</p>
                </div>
              )}
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}

export default PersonalDashboard;
